'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useState } from 'react';

interface DMCAModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function DMCAModal({ isOpen, onClose }: DMCAModalProps) {
    const [language, setLanguage] = useState<'pt' | 'en'>('pt');

    const dmcaContent = {
        pt: {
            title: 'DMCA - Aviso de Infração de Direitos Autorais',
            content: (
                <>
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-3">Notificação de Infração de Direitos Autorais</h3>
                            <p className="mb-4 text-neutral-300 leading-relaxed">
                                O MediocreToons respeita os direitos de propriedade intelectual de terceiros e espera que seus usuários façam o mesmo. 
                                De acordo com o Digital Millennium Copyright Act de 1998, cujo texto pode ser encontrado no site do U.S. Copyright Office 
                                em <a href="http://www.copyright.gov/legislation/dmca.pdf" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 underline">http://www.copyright.gov/legislation/dmca.pdf</a>, 
                                o MediocreToons responderá prontamente a alegações de infração de direitos autorais cometidas usando o serviço e/ou 
                                o site do MediocreToons (o "Serviço") se tais alegações forem relatadas ao Agente de Direitos Autorais Designado 
                                do MediocreToons identificado abaixo.
                            </p>
                        </div>

                        <div className="bg-neutral-800/50 border border-neutral-700 rounded-lg p-5 mt-6">
                            <h3 className="text-lg font-semibold text-white mb-3">Agente de Direitos Autorais Designado</h3>
                            <p className="text-neutral-300 leading-relaxed mb-3">
                                Entre em contato com o Agente de Direitos Autorais Designado do MediocreToons:
                            </p>
                            <p className="text-sm text-neutral-400">
                                <strong className="text-white">E-mail:</strong><br />
                                <a 
                                    href="mailto:mediocre.dmca@gmail.com" 
                                    className="text-orange-400 hover:text-orange-300 transition-colors break-all text-base"
                                >
                                    mediocre.dmca@gmail.com
                                </a>
                            </p>
                        </div>
                    </div>
                </>
            )
        },
        en: {
            title: 'DMCA - Copyright Infringement Notification',
            content: (
                <>
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-3">Copyright Infringement Notification</h3>
                            <p className="mb-4 text-neutral-300 leading-relaxed">
                                MediocreToons respects the intellectual property rights of others and expects its users to do the same. In accordance with 
                                the Digital Millennium Copyright Act of 1998, the text of which may be found on the U.S. Copyright Office website at{' '}
                                <a href="http://www.copyright.gov/legislation/dmca.pdf" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 underline">http://www.copyright.gov/legislation/dmca.pdf</a>, 
                                MediocreToons will respond expeditiously to claims of copyright infringement committed using the MediocreToons service and/or 
                                the MediocreToons website (the "Service") if such claims are reported to MediocreToons's Designated Copyright Agent identified 
                                in the sample notice below.
                            </p>
                        </div>

                        <div className="bg-neutral-800/50 border border-neutral-700 rounded-lg p-5 mt-6">
                            <h3 className="text-lg font-semibold text-white mb-3">Designated Copyright Agent</h3>
                            <p className="text-neutral-300 leading-relaxed mb-3">
                                Contact MediocreToons Designated Copyright Agent:
                            </p>
                            <p className="text-sm text-neutral-400">
                                <strong className="text-white">Email:</strong><br />
                                <a 
                                    href="mailto:mediocre.dmca@gmail.com" 
                                    className="text-orange-400 hover:text-orange-300 transition-colors break-all text-base"
                                >
                                    mediocre.dmca@gmail.com
                                </a>
                            </p>
                        </div>
                    </div>
                </>
            )
        }
    };

    const currentContent = dmcaContent[language];

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Overlay */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm"
                        onClick={onClose}
                    />
                    
                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="fixed inset-0 z-[201] flex items-center justify-center p-4 pointer-events-none"
                    >
                        <div 
                            className="bg-neutral-900 border border-neutral-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col pointer-events-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-800">
                                <h2 className="text-xl font-bold text-white">{currentContent.title}</h2>
                                <div className="flex items-center gap-3">
                                    {/* Language Switcher */}
                                    <div className="flex items-center gap-2 bg-neutral-800 rounded-lg p-1">
                                        <button
                                            onClick={() => setLanguage('pt')}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                                language === 'pt'
                                                    ? 'bg-orange-500 text-white'
                                                    : 'text-neutral-400 hover:text-white'
                                            }`}
                                        >
                                            PT
                                        </button>
                                        <button
                                            onClick={() => setLanguage('en')}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                                language === 'en'
                                                    ? 'bg-orange-500 text-white'
                                                    : 'text-neutral-400 hover:text-white'
                                            }`}
                                        >
                                            EN
                                        </button>
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="p-2 hover:bg-neutral-800 rounded-lg transition-colors text-neutral-400 hover:text-white"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 overflow-y-auto px-6 py-6">
                                <div className="text-base">
                                    {currentContent.content}
                                </div>
                            </div>
                            
                            {/* Footer */}
                            <div className="px-6 py-4 border-t border-neutral-800">
                                <button
                                    onClick={onClose}
                                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                                >
                                    {language === 'pt' ? 'Fechar' : 'Close'}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

